// platformTheme.ts — the OPERATOR PLATFORM CONSOLE design system.
//
// ┌──────────────────────────────────────────────────────────────────────────┐
// │ THIS IS A SEPARATE, SCOPED TOKEN SET. IT DOES NOT TOUCH `T`.              │
// │                                                                          │
// │ `theme.ts`'s `T` is the TENANT PRODUCT surface — Source Serif 4 + Outfit, │
// │ green accent, what customers see. `PT` below is the OPERATOR console —    │
// │ IBM Plex, slate #eef1f6, deliberately different so an operator can tell   │
// │ at a glance which plane they are on.                                      │
// │                                                                          │
// │ DO NOT mutate `T`, and do not add Outfit here. A change to `T` bleeds     │
// │ into every tenant screen, and the console is the one surface that must    │
// │ never be mistaken for the product.                                        │
// └──────────────────────────────────────────────────────────────────────────┘
//
// Import as: import { PT } from '../platformTheme';
// Never: import { T } from '../theme'  — inside the console tree.

import type { CSSProperties } from "react";

export const PT = {
  // ── Surfaces ──────────────────────────────────────────────────────────────
  // #eef1f6 is the console's identity colour: cooler and greyer than the
  // product's #F5F7FA, which is the visual cue that this is the control plane.
  bg: "#eef1f6",
  bg2: "#FFFFFF",
  bg3: "#E4E9F1",
  bg4: "#D8DFEA",
  bgHov: "#E8EDF4",
  bgInset: "#F7F9FC",

  // ── Borders ───────────────────────────────────────────────────────────────
  brd: "#D2D9E4",
  brdSubtle: "#E4E9F1",
  brdStrong: "#B8C2D2",
  brdFocus: "#2F5D8C",

  // ── Text ──────────────────────────────────────────────────────────────────
  tx: "#141A24",
  txM: "#4E5768",
  txD: "#7C859A",
  txW: "#FFFFFF",

  // ── Accent ────────────────────────────────────────────────────────────────
  // Slate blue, NOT the product's green. Operator actions must not look like
  // tenant actions.
  acc: "#2F5D8C",
  accH: "#3A6FA3",
  accS: "rgba(47,93,140,.08)",
  accS2: "rgba(47,93,140,.14)",

  // ── Status ────────────────────────────────────────────────────────────────
  ok: "#1F7A4D", okS: "rgba(31,122,77,.08)",
  warn: "#B87514", warnS: "rgba(184,117,20,.08)",
  err: "#B83232", errS: "rgba(184,50,50,.08)",
  info: "#1A7A8C", infoS: "rgba(26,122,140,.08)",
  neutral: "#5A6478", neutralS: "rgba(90,100,120,.08)",


  // ── Chart + KPI palette (README "Design tokens") ──────────────────────────
  // Added for the fleet Overview. These are CHART colours, not UI accents: the
  // console's own accent stays PT.acc. Kept here so the console still has one
  // scoped token source and never reaches into theme.ts for a colour.
  chBlue: "#2F6FE0",
  chCyan: "#1AA6B7",
  chViolet: "#7C6CF0",
  chGrid: "#EEF1F5",
  chAmber: "#D9A441",

  // Semantic text/soft pairs used by KPI sub-labels and the status donut.
  okTx: "#1F7A48", okSoft: "#E6F6EC",
  warnTx: "#9A6A12", warnSoft: "#FBF1DD",
  errTx: "#C0392B", errSoft: "#FBE9E9",

  // ── Typography ────────────────────────────────────────────────────────────
  // IBM Plex. Sans for UI, Mono for every number.
  fontUI: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",

  // ── Scale ─────────────────────────────────────────────────────────────────
  radius: 4,
  radiusLg: 6,
  space: 8,
} as const;

export type PlatformTheme = typeof PT;

/**
 * Tabular figures for every metric. Without `tnum`, digits shift width between
 * renders and a live-updating number column visibly jitters.
 */
export const PT_NUM: CSSProperties = {
  fontFamily: PT.fontMono,
  fontVariantNumeric: "tabular-nums",
};

/** USD, 2dp. Returns an em dash for null so an empty cell is unambiguous. */
export function fmtCost(v: number | string | null | undefined, currency = "USD"): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  // A small but REAL cost must not render as "$0.00". Groq usage of a third of
  // a cent is genuine spend; showing it at two decimals makes it indistinguishable
  // from a provider we never called, which is the same "empty is not zero"
  // mistake this file's header warns about. Sub-cent amounts get four decimals;
  // an exact zero still shows "$0.00", because that IS zero.
  // EXTENDED 2026-07-30. Four decimals was itself a floor: the per-model
  // breakdown from a provider's invoice carries real charges of $0.000033,
  // which rendered as "$0.0000" — the same "a real cost must not look like
  // nothing" failure this comment already describes, one decimal place further
  // down. The rule is now stated once and applied at every scale: enough
  // decimals to show a non-zero amount as non-zero, capped so the column stays
  // readable. An exact zero still shows "$0.00", because that IS zero.
  const abs = Math.abs(n);
  let dp = 2;
  if (n !== 0) {
    if (abs < 0.000001) dp = 8;
    else if (abs < 0.0001) dp = 6;
    else if (abs < 0.01) dp = 4;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency, minimumFractionDigits: dp, maximumFractionDigits: dp,
  }).format(n);
}

/** Thousands-separated integer. Em dash for null, never "0" — they differ. */
export function fmtNum(v: number | string | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US").format(n);
}

/** Latency in ms, or seconds past 1000ms. Em dash for null. */
export function fmtMs(v: number | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`;
}

/** Token counts abbreviate past 1M — a raw 14-digit token total is unreadable. */
export function fmtTokens(v: number | string | null | undefined): string {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(Math.round(n));
}

/**
 * Error-rate colour. Deliberately conservative: anything at or above 1% is
 * warned, 5% is an error. An operator console that only colours catastrophes
 * teaches people that yellow is normal.
 */
export function rateColor(errors: number, calls: number): string {
  if (!calls) return PT.txD;
  const r = errors / calls;
  if (r >= 0.05) return PT.err;
  if (r >= 0.01) return PT.warn;
  return PT.ok;
}
